import { MenuItemProps } from '@europrocurement/flexy-components/components/molecules';
import { useMemo } from 'react';

/**
 * Hook pour filtrer et transformer les items de menu selon le contexte (mobile/desktop)
 */
export const useMenuFiltering = (items: MenuItemProps[]) => {
    /**
     * Filtre pour desktop/tablet - garde la structure complète
     * Respecte hidden et supprime les items cachés récursivement
     */
    const desktopMenuItems = useMemo(() => {
        const filterItems = (menuItems: MenuItemProps[]): MenuItemProps[] =>
            menuItems.reduce<MenuItemProps[]>((acc, item) => {
                // Ignorer les items cachés
                if (item.hidden) {
                    return acc;
                }

                // Traiter les enfants récursivement si ils existent
                if (item.childrenItems && item.childrenItems.length > 0) {
                    const filteredChildren = filterItems(item.childrenItems);

                    // Garder l'item parent avec ses enfants filtrés
                    acc.push({
                        ...item,
                        childrenItems: filteredChildren,
                    });
                } else {
                    // Garder tous les items leaf (headers inclus pour la structure visuelle)
                    acc.push(item);
                }

                return acc;
            }, []);

        return filterItems(items);
    }, [items]);

    /**
     * Items pour navigation mobile - structure plate des items cliquables uniquement
     * Exclut les headers et respecte hidden
     */
    const mobileNavigationItems = useMemo(() => {
        const flattenItems = (menuItems: MenuItemProps[]): MenuItemProps[] =>
            menuItems.reduce<MenuItemProps[]>((acc, item) => {
                // Ignorer les items cachés
                if (item.hidden) {
                    return acc;
                }

                // Ignorer les headers pour la navigation mobile (ils n'ont pas d'onClick)
                if (!item.header && item.onClick) {
                    acc.push(item);
                }

                // Traiter récursivement les enfants
                if (item.childrenItems && item.childrenItems.length > 0) {
                    acc.push(...flattenItems(item.childrenItems));
                }

                return acc;
            }, []);

        return flattenItems(items);
    }, [items]);

    return {
        desktopMenuItems,
        mobileNavigationItems,
    };
};
